/* eslint-disable no-underscore-dangle */
import axios from 'axios';

import { parseValueIsProd } from '../../utils/formatter';
import { getStorage } from '../../utils/cookies';
import { getAuthLoginUrl } from '../../utils/externalPages';
import { BASE_URL, URL_SANDBOX } from '../../constants/paths';

const { prod, staging, sandbox } = BASE_URL;

const getBaseUrl = (isProd, path) => {
  if (
    process.env.REACT_APP_ENVIRONMENT === 'staging' ||
    process.env.NODE_ENV === 'development'
  ) {
    return staging;
  }

  if (!isProd && URL_SANDBOX.some((element) => element === path)) {
    return sandbox;
  }

  return prod;
};

const API = axios.create();

export function setupApi(store) {
  API.interceptors.request.use(
    (config) => {
      const {
        auth: { authToken, isProduction, enabledProduction, securityCode },
      } = store.getState();
      // eslint-disable-next-line no-param-reassign
      config.baseURL = getBaseUrl(
        parseValueIsProd(enabledProduction, isProduction),
        config.url,
      );

      if (authToken) {
        // eslint-disable-next-line no-param-reassign
        config.headers.Authorization = `Bearer ${authToken}`;
      }

      if (securityCode) {
        // eslint-disable-next-line no-param-reassign
        config.headers.SecurityCode = `${securityCode}`;
      }

      return config;
    },
    (err) => Promise.reject(err),
  );
  API.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      const path = originalRequest?.url;
      const isNotAuthorizedRequestStatus = error.response?.status === 401;
      const errorResponseCode =
        error.response?.data?.error?.error_code ??
        error.response?.data?.error?.code;
      const isNotAuthorizedResponseCode = errorResponseCode === 101;
      const storageRefreshToken = getStorage('auth.refreshToken');
      const storageIsAgent = getStorage('auth.isAgent') === 'true';

      if (
        (isNotAuthorizedRequestStatus && path === '/public/user/refresh') ||
        !storageRefreshToken
      ) {
        window.location.href = getAuthLoginUrl();
        return Promise.reject(error);
      }

      if (!originalRequest._retry) {
        originalRequest._retry = false;
      }

      if (
        isNotAuthorizedRequestStatus &&
        !originalRequest._retry &&
        isNotAuthorizedResponseCode
      ) {
        originalRequest._retry = true;

        try {
          store.dispatch({
            type: '[Set auth] Action',
            payload: {
              authToken: storageRefreshToken,
            },
          });

          const response = await API.post('/public/user/refresh');

          const { data } = response;
          const { token, refresh_token: refreshToken } = data;

          if (token && refreshToken) {
            localStorage.setItem('auth.token', token);
            localStorage.setItem('auth.refreshToken', refreshToken);
            localStorage.setItem('auth.isAgent', storageIsAgent.toString());

            store.dispatch({
              type: '[Login] Action',
              payload: {
                authToken: token,
                isAgent: storageIsAgent,
              },
            });

            return originalRequest;
          }
        } catch (refreshError) {
          window.location.href = getAuthLoginUrl();
        }
      }

      return Promise.reject(error);
    },
  );
}

export default API;
